import { useState, useEffect, useRef, useCallback } from 'react';
import { isSafari } from '../utils/browserDetection';
import { useMediaSession } from './useMediaSession';
import { useWakeLock } from './useWakeLock';

// Статус получения метаданных для диагностики
export type MetadataStatus = 
  | 'idle'           // Начальное состояние
  | 'loading'        // Загрузка метаданных
  | 'success'        // Метаданные получены успешно
  | 'cors_error'     // Ошибка CORS
  | 'network_error'  // Сетевая ошибка
  | 'parse_error'    // Ошибка парсинга
  | 'not_available'; // Метаданные недоступны на сервере

export interface UseRadioPlayerReturn {
  isPlaying: boolean;
  isLoading: boolean;
  hasError: boolean;
  hasEverPlayed: boolean;
  currentTrack: string | null;
  currentArtist: string | null;
  togglePlayback: () => Promise<void>;
  isMSESupported: boolean;
  metadataStatus: MetadataStatus;
}

const STREAM_URL = 'https://afrodita.techit.pro/afrodita_radio';
const SAFARI_CLEANUP_DELAY_MS = 30;
const METADATA_POLL_INTERVAL_MS = 8000; // Оптимизировано: баланс между производительностью и отзывчивостью (best practice: 8-12 сек)
const PLAYBACK_CHECK_INTERVAL_MS = 4000; // Увеличено с 2 до 4 секунд для снижения нагрузки

// Результат получения метаданных с диагностикой
interface MetadataResult {
  streamTitle: string | null;
  status: MetadataStatus;
  error?: string;
}

// Получение метаданных из ICY потока через Fetch API с ReadableStream
const fetchICYMetadata = async (abortController: AbortController, cacheBust: boolean = true): Promise<MetadataResult> => {
  try {
    const timeoutId = setTimeout(() => abortController.abort(), 10000);
    
    const url = cacheBust ? `${STREAM_URL}?t=${Date.now()}` : STREAM_URL;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Icy-MetaData': '1',
      },
      signal: abortController.signal,
      cache: 'no-store',
    });
    
    if (!response.ok) {
      clearTimeout(timeoutId);
      return { streamTitle: null, status: 'network_error', error: `HTTP ${response.status}` };
    }
    
    const icyMetaInt = response.headers.get('icy-metaint');
    if (!icyMetaInt) {
      clearTimeout(timeoutId);
      return { streamTitle: null, status: 'not_available', error: 'Сервер не поддерживает ICY метаданные' };
    }
    
    const metadataInterval = parseInt(icyMetaInt, 10);
    
    if (!response.body) {
      clearTimeout(timeoutId);
      return { streamTitle: null, status: 'not_available', error: 'ReadableStream не поддерживается' };
    }
    
    const reader = response.body.getReader();
    let buffer = new Uint8Array(0);
    
    try {
      while (buffer.length < metadataInterval + 4096) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        const newBuffer = new Uint8Array(buffer.length + value.length);
        newBuffer.set(buffer);
        newBuffer.set(value, buffer.length);
        buffer = newBuffer;
        
        if (buffer.length > metadataInterval) {
          const metadataLengthByte = buffer[metadataInterval];
          const metadataLength = metadataLengthByte * 16;
          
          if (metadataLength > 0 && buffer.length >= metadataInterval + 1 + metadataLength) {
            const metadataBytes = buffer.slice(metadataInterval + 1, metadataInterval + 1 + metadataLength);
            const metadataString = new TextDecoder('utf-8').decode(metadataBytes);
            
            const streamTitleStart = metadataString.indexOf("StreamTitle='");
            if (streamTitleStart !== -1) {
              const valueStart = streamTitleStart + "StreamTitle='".length;
              let valueEnd = metadataString.indexOf("';", valueStart);
              if (valueEnd === -1) {
                valueEnd = metadataString.indexOf("'", valueStart);
              }
              
              if (valueEnd !== -1) {
                const streamTitle = metadataString
                  .substring(valueStart, valueEnd)
                  .trim()
                  .replace(/\0/g, '');
                if (streamTitle && streamTitle !== '0') {
                  reader.cancel();
                  clearTimeout(timeoutId);
                  return { streamTitle, status: 'success' };
                }
              }
            }
          }
        }
      }
    } finally {
      reader.cancel().catch(() => {});
    }
    
    clearTimeout(timeoutId);
    return { streamTitle: null, status: 'not_available', error: 'Метаданные не найдены в потоке' };
    
  } catch (error: any) {
    if (error.name === 'AbortError') {
      return { streamTitle: null, status: 'network_error', error: 'Запрос отменен' };
    }
    return { streamTitle: null, status: 'cors_error', error: String(error) };
  }
};

// Парсинг StreamTitle в формат "Artist - Title"
const parseStreamTitle = (streamTitle?: string): { title: string; artist: string } | null => {
  if (!streamTitle || !streamTitle.trim()) {
    return null;
  }

  const cleanTitle = streamTitle.trim().replace(/\0/g, '');
  const parts = cleanTitle.split(' - ').map(part => part.trim()).filter(part => part.length > 0);
  
  if (parts.length >= 2) {
    return {
      artist: parts[0],
      title: parts.slice(1).join(' - '),
    };
  } else if (parts.length === 1) {
    return {
      title: parts[0],
      artist: '',
    };
  }

  return null;
};


export const useRadioPlayer = (): UseRadioPlayerReturn => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [hasEverPlayed, setHasEverPlayed] = useState<boolean>(false);
  const [isMSESupported, setIsMSESupported] = useState<boolean>(false);
  const [audioReconnectNonce, setAudioReconnectNonce] = useState<number>(0);
  
  // Состояния для метаданных (объединены из useMetadata)
  const [currentTrack, setCurrentTrack] = useState<string | null>(null);
  const [currentArtist, setCurrentArtist] = useState<string | null>(null);
  const [metadataStatus, setMetadataStatus] = useState<MetadataStatus>('idle');

  const { requestWakeLock, releaseWakeLock, cleanup: cleanupWakeLock } = useWakeLock();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const unifiedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const togglePlaybackRef = useRef<(() => Promise<void>) | null>(null);
  
  const userPausedRef = useRef(false);
  const reconnectingRef = useRef(false);
  const retryAttemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTimeRef = useRef(0);
  const lastProgressAtRef = useRef<number>(Date.now());

  // Refs для метаданных (объединены из useMetadata)
  const metadataAbortRef = useRef<AbortController | null>(null);
  const metadataLastUpdateAtRef = useRef<number>(0);
  const metadataLastUpdateRef = useRef<number>(0);
  const metadataIsUpdatingRef = useRef<boolean>(false);

  // Сброс метаданных
  const resetMetadata = useCallback(() => {
    metadataAbortRef.current?.abort();
    setCurrentTrack(null);
    setCurrentArtist(null);
    setMetadataStatus('idle');
    metadataLastUpdateAtRef.current = 0;
    metadataLastUpdateRef.current = 0;
  }, []);

  // Обновление метаданных (объединено из useMetadata)
  const updateMetadata = useCallback(async (force: boolean = false) => {
    const now = Date.now();
    if (!force && (metadataIsUpdatingRef.current || (now - metadataLastUpdateRef.current < METADATA_POLL_INTERVAL_MS - 1000))) {
      return;
    }
    
    metadataAbortRef.current?.abort();
    const controller = new AbortController();
    metadataAbortRef.current = controller;
    
    metadataIsUpdatingRef.current = true;
    metadataLastUpdateRef.current = now;
    setMetadataStatus('loading');
    
    try {
      const result = await fetchICYMetadata(controller, true);
      
      if (controller.signal.aborted) {
        return;
      }
      
      if (result.streamTitle) {
        const parsed = parseStreamTitle(result.streamTitle);
        
        if (parsed) {
          setCurrentTrack(parsed.title);
          setCurrentArtist(parsed.artist || null);
          setMetadataStatus('success');
        } else {
          setCurrentTrack(result.streamTitle);
          setCurrentArtist(null);
          setMetadataStatus('success');
        }
        metadataLastUpdateAtRef.current = Date.now();
      } else {
        setMetadataStatus(result.status);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }
      setMetadataStatus('network_error');
    } finally {
      metadataIsUpdatingRef.current = false;
    }
  }, []);

  // Основная функция переключения воспроизведения
  const togglePlayback = useCallback(async (): Promise<void> => {
    if (!audioRef.current) return;

    const safari = isSafari();

    if (hasError) {
      setHasError(false);
    }

    try {
      if (isPlaying) {
        userPausedRef.current = true;
        audioRef.current.pause();
      } else {
        const audio = audioRef.current;
        const needsNewSrc = !audio.src || audio.src === window.location.href || !audio.src.includes('afrodita_radio');
        
        if (needsNewSrc) {
          const streamUrlWithCacheBust = `${STREAM_URL}?t=${Date.now()}`;
          
          if (audio.src) {
            audio.src = '';
            audio.load();
            if (safari) {
              await new Promise(resolve => setTimeout(resolve, SAFARI_CLEANUP_DELAY_MS));
            }
          }
          
          audio.src = streamUrlWithCacheBust;
          setIsLoading(true);
          setHasError(false);
          audio.load();
          
          if (safari) {
            await new Promise(resolve => setTimeout(resolve, SAFARI_CLEANUP_DELAY_MS));
          }
        } else {
          setIsLoading(false);
        }
        
        userPausedRef.current = false;
        retryAttemptRef.current = 0;
        setHasError(false);
        const playPromise = audio.play();
        
        if (playPromise !== undefined) {
          await playPromise;
        }
      }
    } catch (error: any) {
      console.error('Ошибка воспроизведения:', error);
      
      if (safari && audioRef.current) {
        const audio = audioRef.current;
        
        if (error?.name === 'NotSupportedError' || audio.error?.code === 4) {
          try {
            audio.src = '';
            audio.load();
            await new Promise(resolve => setTimeout(resolve, SAFARI_CLEANUP_DELAY_MS * 2));
            
            const retryUrl = `${STREAM_URL}?t=${Date.now()}`;
            audio.src = retryUrl;
            audio.load();
            
            if (safari) {
              await new Promise(resolve => setTimeout(resolve, SAFARI_CLEANUP_DELAY_MS * 2));
            }
            
            await audio.play();
          } catch (retryError) {
            // Игнорируем ошибки повторной попытки
          }
        }
      }
      
      setIsLoading(false);
      setIsPlaying(false);
      setHasError(true);
    }
  }, [isPlaying, hasError]);

  // Жёсткий reconnect для восстановления соединения
  const hardReconnect = useCallback(async (reason: string) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (userPausedRef.current) return;
    if (reconnectingRef.current) return;

    reconnectingRef.current = true;

    setAudioReconnectNonce(prev => prev + 1);

    const attempt = retryAttemptRef.current++;
    const backoffMs = Math.min(30000, 500 * Math.pow(2, attempt));

    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    retryTimerRef.current = setTimeout(async () => {
      if (userPausedRef.current) {
        reconnectingRef.current = false;
        retryTimerRef.current = null;
        return;
      }
      try {
        setIsLoading(true);
        setHasError(false);

        audio.pause();
        audio.src = '';
        audio.load();

        const safari = isSafari();
        if (safari) await new Promise(r => setTimeout(r, SAFARI_CLEANUP_DELAY_MS));

        audio.src = `${STREAM_URL}?t=${Date.now()}`;
        audio.load();

        if (safari) await new Promise(r => setTimeout(r, SAFARI_CLEANUP_DELAY_MS));

        await audio.play();
        retryAttemptRef.current = 0;
      } catch {
        setHasError(true);
        setIsPlaying(false);
        setIsLoading(false);
      } finally {
        reconnectingRef.current = false;
        retryTimerRef.current = null;
      }
    }, backoffMs);
  }, []);

  // Сохраняем ссылку на togglePlayback для Media Session
  useEffect(() => {
    togglePlaybackRef.current = togglePlayback;
  }, [togglePlayback]);

  // Используем MediaSession хук
  useMediaSession({
    track: currentTrack,
    artist: currentArtist,
    isPlaying,
    onPlay: togglePlayback,
    onPause: async () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    },
  });

  // Проверка поддержки Media Source Extensions
  useEffect(() => {
    const mseSupported = typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg');
    setIsMSESupported(mseSupported);
  }, []);

  // Инициализация аудио плеера
  useEffect(() => {
    if (audioRef.current) {
      return;
    }

    const audio = new Audio();
    audio.preload = 'none';
    audio.crossOrigin = 'anonymous';
    audio.setAttribute('playsinline', 'true');
    audio.setAttribute('webkit-playsinline', 'true');
    audio.setAttribute('x-webkit-airplay', 'allow');
    audioRef.current = audio;

    const handlePlay = async () => {
      setIsPlaying(true);
      setIsLoading(false);
      setHasError(false);
      setHasEverPlayed(true);
      
      await requestWakeLock();
      updateMetadata();
    };

    const handlePause = async () => {
      setIsPlaying(false);
      setIsLoading(false);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
      setHasError(false);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setHasError(false);
    };

    const handleWaiting = () => {
      setIsLoading(true);
    };

    const handleStalled = () => {
      setIsLoading(true);
    };

    const handleError = (e: Event) => {
      setIsLoading(false);
      setIsPlaying(false);
      setHasError(true);
      const target = e.target as HTMLAudioElement;
      console.error('[Player] Ошибка загрузки аудио потока:', {
        error: target.error,
        code: target.error?.code,
        message: target.error?.message || '',
        networkState: target.networkState,
        readyState: target.readyState,
      });
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('stalled', handleStalled);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('stalled', handleStalled);
      audio.removeEventListener('error', handleError);
      
      if (audio) {
        audio.pause();
        audio.src = '';
        audio.load();
      }
      audioRef.current = null;
    };
  }, [requestWakeLock, updateMetadata]);

  // Watchdog для метаданных интегрирован в unifiedIntervalRef для оптимизации

  // Синхронизация с аудио reconnect (объединено из useMetadata)
  useEffect(() => {
    if (audioReconnectNonce > 0) {
      resetMetadata();
      updateMetadata(true);
    }
  }, [audioReconnectNonce, resetMetadata, updateMetadata]);

  // Объединенный интервал для метаданных и проверки воспроизведения (оптимизирован)
  useEffect(() => {
    if (!isPlaying) {
      if (unifiedIntervalRef.current) {
        clearInterval(unifiedIntervalRef.current);
        unifiedIntervalRef.current = null;
      }
      return;
    }

    let metadataUpdateCounter = 0;
    const METADATA_UPDATE_INTERVAL = Math.ceil(METADATA_POLL_INTERVAL_MS / PLAYBACK_CHECK_INTERVAL_MS);

    unifiedIntervalRef.current = setInterval(() => {
      // Останавливаем интервал при неактивной вкладке для экономии ресурсов
      if (document.hidden) {
        return;
      }

      const audio = audioRef.current;
      if (!audio) return;

      const now = Date.now();
      const t = audio.currentTime;

      // Проверка застревания воспроизведения (оптимизирована)
      if (!audio.paused && isFinite(t)) {
        if (t !== lastTimeRef.current) {
          lastTimeRef.current = t;
          lastProgressAtRef.current = now;
        } else {
          const stuckMs = now - lastProgressAtRef.current;
          if (stuckMs > 5000) {
            hardReconnect('watchdog_stuck');
            return;
          }
        }
      }

      // Проверка несоответствия состояния
      if (audio.paused && isPlaying && !userPausedRef.current) {
        hardReconnect('paused_while_should_play');
        return;
      }

      // Watchdog для метаданных (интегрирован из отдельного интервала)
      const metadataAge = now - metadataLastUpdateAtRef.current;
      if (metadataAge > 12000) { // 12 секунд (1.5 × интервал обновления) для надежности
        metadataAbortRef.current?.abort();
        updateMetadata(true);
        return;
      }

      // Обновление метаданных по расписанию
      metadataUpdateCounter++;
      if (metadataUpdateCounter >= METADATA_UPDATE_INTERVAL && !audio.paused) {
        metadataUpdateCounter = 0;
        updateMetadata();
      }
    }, PLAYBACK_CHECK_INTERVAL_MS);

    return () => {
      if (unifiedIntervalRef.current) {
        clearInterval(unifiedIntervalRef.current);
        unifiedIntervalRef.current = null;
      }
    };
  }, [isPlaying, updateMetadata, hardReconnect]);

  // Объединенная обработка всех window/document событий
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && audioRef.current && isPlaying && audioRef.current.paused && !userPausedRef.current) {
        const audio = audioRef.current;
        if (audio.src && audio.src !== window.location.href) {
          audio.play().catch(() => {});
        } else if (togglePlaybackRef.current) {
          togglePlaybackRef.current().catch(() => {});
        }
      }
      
      if (!document.hidden && isPlaying) {
        requestWakeLock().catch(() => {});
      } else if (document.hidden) {
        releaseWakeLock().catch(() => {});
      }
    };

    const handleOnline = () => {
      if (isPlaying && !userPausedRef.current) {
        hardReconnect('online');
      }
      // Обновляем метаданные при восстановлении сети
      if (isPlaying) {
        updateMetadata(true);
      }
    };

    const handleOffline = () => {
      setIsLoading(false);
      metadataAbortRef.current?.abort();
    };

    const handleWindowBlur = () => {
      if (audioRef.current && isPlaying && audioRef.current.paused && !userPausedRef.current) {
        audioRef.current.play().catch(() => {});
      }
    };

    const handleWindowFocus = () => {
      if (audioRef.current && isPlaying && audioRef.current.paused && !userPausedRef.current) {
        if (togglePlaybackRef.current) {
          togglePlaybackRef.current().catch(() => {});
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [isPlaying, requestWakeLock, releaseWakeLock, hardReconnect, updateMetadata]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      cleanupWakeLock();
      if (unifiedIntervalRef.current) {
        clearInterval(unifiedIntervalRef.current);
        unifiedIntervalRef.current = null;
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      metadataAbortRef.current?.abort();
      metadataAbortRef.current = null;
    };
  }, [cleanupWakeLock]);

  return {
    isPlaying,
    isLoading,
    hasError,
    hasEverPlayed,
    currentTrack,
    currentArtist,
    togglePlayback,
    isMSESupported,
    metadataStatus,
  };
};
